import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

interface Options {
  flightCreditFee: number
  currency: string
}

function useFlightCreditTerms({ flightCreditFee, currency }: Options): Array<string> {
  const intl = useIntl()
  const formatCurrency = useCurrencyFormatter({ currency })

  return useMemo(() => {
    const fee = formatCurrency(flightCreditFee, 'roundedDollar')

    return [
      intl.formatMessage({
        id: 'Jm7PERn3fXexd2IOymB1',
        defaultMessage: 'Fare rules of your new ticket will be the same as on your old ticket.',
        description: 'Flight credit rebooking term 1 - fare rules carry over',
      }),
      intl.formatMessage({
        id: 'HakmP-LKHMlmOQQpfDrT',
        defaultMessage: 'Airline fee - to be advised.',
        description: 'Flight credit rebooking term 2 - airline fee notice',
      }),
      intl.formatMessage(
        {
          id: 'Ern2LStDoGsA-J4t1tS2',
          defaultMessage: 'Luxury Escapes reduced service fee - {fee} per person, per ticket.',
          description: 'Flight credit rebooking term 3 - service fee with dynamic amount and currency',
        },
        { fee },
      ),
      intl.formatMessage({
        id: 'tfL52gGq7FT6JQpinH1a',
        defaultMessage: 'New fare must be equal or higher.',
        description: 'Flight credit rebooking term 4 - fare value requirement',
      }),
      intl.formatMessage({
        id: 'hzmLMg5-1bB5TB0jti37',
        defaultMessage: 'Fare and/or tax difference (if any) will need to be paid by the customer.',
        description: 'Flight credit rebooking term 5 - fare difference responsibility',
      }),
      intl.formatMessage({
        id: 'YhXJeHDUdSYKQkGBoh9F',
        defaultMessage: 'Your new flight must depart from the same country as was shown on your original ticket.',
        description: 'Flight credit rebooking term 6 - departure country restriction',
      }),
      intl.formatMessage({
        id: '21sl9Ycru-LKSfcUbM8M',
        defaultMessage: 'Value of your ticket credit cannot be used on partner airlines (codeshare).',
        description: 'Flight credit rebooking term 7 - codeshare restriction',
      }),
      intl.formatMessage({
        id: 'oDe4yIi6WiN_CzQpiOX8',
        defaultMessage: 'Value of your ticket credit must be used in a single transaction.',
        description: 'Flight credit rebooking term 8 - single transaction requirement',
      }),
      intl.formatMessage({
        id: 'mJxZ-6Ew_GyP5Vtvipvq',
        defaultMessage: 'Name on your new flight ticket must be the same as on your old ticket.',
        description: 'Flight credit rebooking term 9 - name must match in the flights section',
      }),
      intl.formatMessage({
        id: 'DZ2EU7QMbhz-lTg4epuS',
        defaultMessage: 'Value of your ticket credit is non-refundable, non-transferrable, and non-combinable.',
        description: 'Flight credit rebooking term 10 - credit restrictions',
      }),
      intl.formatMessage({
        id: 'kRtipnc44k1oaOZ9U9_1',
        defaultMessage: 'Your airline credit comes with a travel-by date. Please ensure to check this information on the credit confirmation that was previously sent to you.',
        description: 'Flight credit rebooking term 11 - travel-by date notice',
      }),
      intl.formatMessage({
        id: 'FbRf4v9WA1N_LafLpU5R',
        defaultMessage: 'Airline credit conditions are subject to change without prior notice.',
        description: 'Flight credit rebooking term 12 - conditions may change notice',
      }),
    ]
  }, [intl, formatCurrency, flightCreditFee])
}

export default useFlightCreditTerms
